import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Badge, Form, Button } from 'react-bootstrap';
import { FaEdit } from 'react-icons/fa';
import { api } from '../../config/api';
import ImageViewerButton from '../layout/ImageViewerButton';

export default function SharedOrderDetailModal({ 
  show, 
  onHide, 
  order: initialOrder, 
  orderId, 
  readOnly = false,
  onSaveOrder,
}) {
  const [order, setOrder] = useState(null);
  const [draftOrder, setDraftOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [slipFile, setSlipFile] = useState(null);
  const [slipPreviewUrl, setSlipPreviewUrl] = useState('');

  useEffect(() => {
    if (!show) {
      setSlipFile(null);
      setSlipPreviewUrl('');
      return;
    }

    if (initialOrder) {
      setOrder(initialOrder);
      setDraftOrder(JSON.parse(JSON.stringify(initialOrder)));
      setSlipPreviewUrl(initialOrder.slip_image_url || '');
    } else if (orderId) {
      const fetchOrder = async () => {
        setLoading(true);
        try {
          const token = localStorage.getItem('token');
          const res = await api.get(`/api/orders/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setOrder(res.data);
          setDraftOrder(JSON.parse(JSON.stringify(res.data)));
          setSlipPreviewUrl(res.data.slip_image_url || '');
        } catch (err) {
          console.error("Failed to fetch order", err);
        } finally {
          setLoading(false);
        }
      };
      fetchOrder();
    }
  }, [show, initialOrder, orderId]);

  // Keep order updated if initialOrder changes (SSE updates)
  useEffect(() => {
    if (initialOrder && show) {
      setOrder(initialOrder);
      setSlipPreviewUrl(initialOrder.slip_image_url || '');
      // Only update draft if we haven't modified anything to prevent overwriting user input?
      // For simplicity, we just sync it, assuming SSE updates come when another user edits.
      setDraftOrder(JSON.parse(JSON.stringify(initialOrder)));
    }
  }, [initialOrder, show]);

  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/api/orders/${order?.id || orderId}/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data);
    } catch (err) {
      console.error("Failed to fetch logs", err);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleShowLogs = () => {
    fetchLogs();
    setShowLogs(true);
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending': return <Badge bg="secondary">Pending</Badge>;
      case 'cooking': return <Badge bg="warning" text="dark">Cooking</Badge>;
      case 'ready': return <Badge bg="primary">Ready to Deliver</Badge>;
      case 'completed': return <Badge bg="success">Completed</Badge>;
      case 'cancelled': return <Badge bg="danger">Cancelled</Badge>;
      default: return <Badge bg="dark">{status}</Badge>;
    }
  };

  const handleSlipUpdate = (file) => {
    setSlipFile(file);
    setSlipPreviewUrl(URL.createObjectURL(file));
  };

  const handleSlipDelete = () => {
    setSlipFile(null);
    setSlipPreviewUrl('');
    setDraftOrder(prev => ({ ...prev, slip_image_url: null }));
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    if (newStatus === 'cancelled') {
      if (!window.confirm("คุณแน่ใจหรือไม่ที่จะยกเลิกออเดอร์นี้? รายการที่ถูกยกเลิกแล้วจะไม่สามารถเปลี่ยนสถานะกลับได้อีก")) {
        return;
      }
    }
    setDraftOrder({...draftOrder, status: newStatus});
  };

  const handleSaveAll = async () => {
    if (onSaveOrder) {
      setSaving(true);
      await onSaveOrder(draftOrder, slipFile);
      setSaving(false);
    }
  };

  // Helper to format datetime-local input
  const formatForInput = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };

  const subtotal = order ? (Number(order.total_price) + Number(order.discount || 0)) : 0;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="border-0 pb-0 d-flex justify-content-between align-items-center">
        <Modal.Title className="fw-bold text-primary me-3">
          Order Details #{order?.id || orderId}
        </Modal.Title>
        <div>
          <Button variant="outline-info" size="sm" className="me-3" onClick={handleShowLogs}>
            Edit History
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : order ? (
          <>
            <div className="bg-light p-3 rounded-3 mb-4">
              <Row>
                <Col md={4} sm={6} className="mb-3 mb-md-0">
                  <p className="mb-1 text-muted small">Recipient</p>
                  {readOnly ? (
                    <p className="fw-bold mb-3">{draftOrder.recipient_name || '-'}</p>
                  ) : (
                    <Form.Control 
                      size="sm"
                      type="text"
                      className="mb-3 fw-bold"
                      value={draftOrder.recipient_name || ''}
                      onChange={(e) => setDraftOrder({...draftOrder, recipient_name: e.target.value})}
                    />
                  )}
                  
                  <p className="mb-1 text-muted small">Order Time</p>
                  <p className="fw-bold mb-0">{new Date(draftOrder.created_at).toLocaleString('en-GB')}</p>
                  
                  {readOnly && draftOrder.delivery_address && (
                    <>
                      <p className="mb-1 text-muted small mt-3">Delivery Address</p>
                      <p className="fw-bold mb-0">{draftOrder.delivery_address}</p>
                    </>
                  )}
                </Col>

                <Col md={4} sm={6} className="mb-3 mb-md-0">
                  <p className="mb-1 text-muted small">Status</p>
                  <div className="mb-3 d-flex align-items-center gap-2">
                    {getStatusBadge(draftOrder.status)}
                    {!readOnly && (
                      <Form.Select 
                        size="sm" 
                        style={{ width: 'auto' }} 
                        value={draftOrder.status}
                        onChange={handleStatusChange}
                        disabled={order.status === 'completed' || order.status === 'cancelled'}
                      >
                        {order.status !== 'completed' && order.status !== 'cancelled' && (
                          <>
                            <option value="pending">Pending</option>
                            <option value="cooking">Cooking</option>
                            <option value="ready">Ready</option>
                            <option value="completed">Completed</option>
                          </>
                        )}
                        <option value="cancelled">Cancelled</option>
                        {order.status === 'completed' && <option value="completed">Completed</option>}
                      </Form.Select>
                    )}
                  </div>

                  <p className="mb-1 text-muted small">Delivery Time</p>
                  {readOnly ? (
                    <p className="fw-bold text-danger mb-0">
                      {draftOrder.delivery_time ? new Date(draftOrder.delivery_time).toLocaleString('en-GB') : 'As soon as possible'}
                    </p>
                  ) : (
                    <Form.Control
                      size="sm"
                      type="datetime-local"
                      className="mb-0 fw-bold text-danger"
                      value={formatForInput(draftOrder.delivery_time)}
                      onChange={(e) => setDraftOrder({...draftOrder, delivery_time: e.target.value || null})}
                    />
                  )}
                </Col>

                <Col md={4} sm={12}>
                  <p className="mb-1 text-muted small">Payment ({draftOrder.payment_method})</p>
                  <div className="mb-3 d-flex align-items-center gap-2">
                    <Badge bg={draftOrder.payment_status === 'paid' ? 'success' : 'warning'} className="text-uppercase">
                      {draftOrder.payment_status || 'unpaid'}
                    </Badge>
                    {!readOnly && (
                      <Form.Select 
                        size="sm" 
                        style={{ width: 'auto' }} 
                        value={draftOrder.payment_status || 'unpaid'}
                        onChange={(e) => setDraftOrder({...draftOrder, payment_status: e.target.value})}
                      >
                        <option value="unpaid">Unpaid</option>
                        <option value="paid">Paid</option>
                      </Form.Select>
                    )}
                  </div>
                  
                  <div className="mt-3">
                    <p className="mb-1 text-muted small">Payment Slip (สลิปโอนเงิน)</p>
                    {slipPreviewUrl ? (
                      <div className="d-flex align-items-center gap-2">
                        <ImageViewerButton 
                          imageUrl={slipPreviewUrl}
                          onUpdate={!readOnly ? handleSlipUpdate : null}
                          onDelete={!readOnly ? handleSlipDelete : null}
                          buttonText="ดูรูปสลิป"
                        />
                      </div>
                    ) : (
                      !readOnly && (
                        <Form.Group className="m-0">
                          <Form.Label className="btn btn-outline-secondary btn-sm m-0" style={{ cursor: 'pointer' }}>
                            Attach Slip (แนบรูปสลิป)
                            <Form.Control 
                              type="file" 
                              accept="image/*" 
                              style={{ display: 'none' }} 
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) handleSlipUpdate(file);
                              }} 
                            />
                          </Form.Label>
                        </Form.Group>
                      )
                    )}
                  </div>
                </Col>
              </Row>
            </div>

            <h6 className="fw-bold border-bottom pb-2 mb-3">Order Items</h6>
            <div className="bg-light p-3 rounded-3 mb-4">
              {order.items?.map(item => {
                let itemTotal = Number(item.price_at_sale);
                item.modifiers?.forEach(m => itemTotal += Number(m.price_at_sale));
                return (
                  <div key={item.id} className="d-flex justify-content-between mb-3 border-bottom pb-2 last-border-none">
                    <div>
                      <div className="fw-bold">{item.quantity}x {item.product_name}</div>
                      {item.modifiers?.length > 0 && (
                        <div className="text-muted small ms-3 mt-1">
                          {item.modifiers.map(m => (
                            <div key={m.id}>+ {m.modifier_name} (฿{Number(m.price_at_sale)})</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="fw-bold text-primary">
                      ฿{(itemTotal * item.quantity).toLocaleString()}
                    </div>
                  </div>
                );
              })}
            </div>

            <Row>
              <Col md={12}>
                <div className="bg-primary bg-opacity-10 p-3 rounded-3 text-end mt-3 mt-md-0 ms-auto" style={{ maxWidth: '400px' }}>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Subtotal</span>
                    <span className="fw-bold">฿{subtotal.toLocaleString()}</span>
                  </div>
                  
                  {order.discounts && order.discounts.length > 0 ? (
                    order.discounts.map((disc, i) => (
                      <div key={i} className="d-flex justify-content-between mb-2 text-danger small">
                        <span>Discount ({disc.discount_name})</span>
                        <span className="fw-bold">-฿{Number(disc.calculated_discount_amount).toLocaleString()}</span>
                      </div>
                    ))
                  ) : Number(order.discount) > 0 ? (
                    <div className="d-flex justify-content-between mb-2 text-danger small">
                      <span>Discount</span>
                      <span className="fw-bold">-฿{Number(order.discount).toLocaleString()}</span>
                    </div>
                  ) : null}

                  <div className="border-top border-primary border-opacity-25 my-2"></div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted fw-bold">Total Amount</span>
                    <h3 className="fw-bold text-primary m-0">฿{Number(order.total_price).toLocaleString()}</h3>
                  </div>
                </div>
              </Col>
            </Row>
          </>
        ) : (
          <div className="text-center py-5">
            <p className="text-muted">Order not found.</p>
          </div>
        )}
      </Modal.Body>
      {!readOnly && !loading && order && (
        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={onHide}>Close</Button>
          <Button variant="primary" onClick={handleSaveAll} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      )}

      {/* Edit History Sub-Modal */}
      <Modal show={showLogs} onHide={() => setShowLogs(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h5 mb-0">Edit History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingLogs ? (
            <div className="text-center py-4">Loading logs...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-4 text-muted">No edit history found for this order.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover small">
                <thead className="table-light">
                  <tr>
                    <th>Date/Time</th>
                    <th>Edited By</th>
                    <th>Changes</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const oldVals = JSON.parse(log.old_values || '{}');
                    const newVals = JSON.parse(log.new_values || '{}');
                    const keys = Object.keys(newVals);
                    
                    return (
                      <tr key={log.id}>
                        <td>{new Date(log.created_at).toLocaleString('en-GB')}</td>
                        <td>{log.edited_by}</td>
                        <td>
                          {keys.map(k => (
                            <div key={k} className="mb-1">
                              <strong>{k}:</strong> <span className="text-danger text-decoration-line-through">{String(oldVals[k])}</span> <span className="text-success">&rarr; {String(newVals[k])}</span>
                            </div>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Modal>
  );
}
